import mixpanel from 'mixpanel-browser'
import React, { useEffect } from 'react'
import { useRecoilState } from 'recoil'
import { useGetUserSubscriptionLazyQuery, useValidateUserIdentityMutation } from '../api/client'
import Loader from '../components/Loader'
import NoActiveSubscription from '../components/subscription/NoActiveSubscription'
import { PaymentPageRedirect } from '../components/subscription/PaymentPageRedirect'
import LoadingWelcome from '../components/unauthenticated/LoadingWelcome'
import useQueryParams from '../hooks/useQueryParams'
import User from '../models/User'
import {
    canUserLikeContentState,
    canUserReplyToContentState,
    canUseCrossProfileEngageState,
    canChangeCrossProfileEngageDefaultState,
    isPlatformAppReviewerState,
    isUserAdminState,
    userState,
    canManageShadowBanningState,
    referrerState,
    keywordsEnabledState,
    canViewScreenshotsState,
    askArwenEnabledState,
    canLabelSpamState,
    canHideContentState,
    canDeleteContentState,
    canBlockUserState,
    canMuteUserState,
    conversationViewEnabledState
} from '../store/DomainStore'
import { getUserPermissions } from '../util/auth'
import { EXCLUDE_OWN } from '../models/QueryParameters'

type Props = {
    children?: React.ReactNode
}

/**
 * This container fetches all data required from the server
 * for correct functioning of the rest of the application.
 * For example, we need the user details in the store
 * before being able to proceed with subscription
 */
function AppInitialisationContainer(props: Props) {
    const { children } = props

    const query = useQueryParams()
    const referrerUuid = query.get('referrer')

    // See the validateUserMutation docs in Rust graphql for an explanation around why this exists.
    const [validateUserMutation] = useValidateUserIdentityMutation({
        onCompleted: (user) => {
            console.log('User identity validated')

            getUserSubscription({ variables: { referrer: referrerUuid } })
            setCanUserReplyToContent(user.validateUserIdentity.canReplyToContent)
            setCanChangeCrossProfileEngageDefault(
                user.validateUserIdentity.toggles.canModifyCrossProfileDefault
            )
            setCanUseCrossProfileEngage(user.validateUserIdentity.toggles.canEngageCrossProfile)
            setCanManageShadowBanning(user.validateUserIdentity.toggles.canManageShadowBanning)
            setKeywordsEnabled(user.validateUserIdentity.toggles.keywordsEnabled)
            setCanViewScreenshots(user.validateUserIdentity.toggles.canViewScreenshots)
            setAskArwenEnabled(user.validateUserIdentity.toggles.askArwenEnabled)
            setCanLabelSpam(user.validateUserIdentity.toggles.canLabelSpam)
            setCanHideContent(user.validateUserIdentity.toggles.canHideContent)
            setCanDeleteContent(user.validateUserIdentity.toggles.canDeleteContent)
            setCanBlockUser(user.validateUserIdentity.toggles.canBlockUser)
            setCanMuteUser(user.validateUserIdentity.toggles.canMuteUser)
            setConversationViewEnabled(user.validateUserIdentity.toggles.conversationViewEnabled)
        }
    })

    useEffect(() => {
        validateUserMutation({ variables: { referrerUuid } })
        // eslint-disable-next-line
    }, [])

    const [
        getUserSubscription,
        { data: subscriptionData, loading: subscriptionLoading, error: subscriptionError }
    ] = useGetUserSubscriptionLazyQuery()

    /* eslint-disable @typescript-eslint/no-unused-vars */
    const [user, setUser] = useRecoilState(userState)
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const [isUserAdmin, setIsUserAdmin] = useRecoilState(isUserAdminState)
    const [canUserLikeContent, setCanUserLikeContent] = useRecoilState(canUserLikeContentState)
    const [canUserReplyToContent, setCanUserReplyToContent] = useRecoilState(
        canUserReplyToContentState
    )
    const [, setCanManageShadowBanning] = useRecoilState(canManageShadowBanningState)
    const [, setCanUseCrossProfileEngage] = useRecoilState(canUseCrossProfileEngageState)
    const [, setCanChangeCrossProfileEngageDefault] = useRecoilState(
        canChangeCrossProfileEngageDefaultState
    )
    const [, setKeywordsEnabled] = useRecoilState(keywordsEnabledState)
    const [, setCanViewScreenshots] = useRecoilState(canViewScreenshotsState)
    const [, setAskArwenEnabled] = useRecoilState(askArwenEnabledState)
    const [, setCanLabelSpam] = useRecoilState(canLabelSpamState)
    const [, setCanHideContent] = useRecoilState(canHideContentState)
    const [, setCanDeleteContent] = useRecoilState(canDeleteContentState)
    const [, setCanBlockUser] = useRecoilState(canBlockUserState)
    const [, setCanMuteUser] = useRecoilState(canMuteUserState)
    const [, setConversationViewEnabled] = useRecoilState(conversationViewEnabledState)
    const [isPlatformAppReviewer, setPlatformAppReviewer] = useRecoilState(
        isPlatformAppReviewerState
    )

    // Initialise the defaults for user settings held in local storage
    // EXCLUDE_OWN
    if (localStorage.getItem(EXCLUDE_OWN) === null) {
        // Set the default
        localStorage.setItem(EXCLUDE_OWN, String(true))
    }

    const [referrer, setReferrer] = useRecoilState(referrerState)

    useEffect(() => {
        if (subscriptionData) {
            const userData = subscriptionData.getUserSubscription as User
            setUser(userData)
            setReferrer(userData.referrer)
            if (userData.username) {
                // There is a user so we need to identify them in mixpanel. This will allow us to track their actions.
                mixpanel.identify(userData.username)
            }

            setCanUserLikeContent(subscriptionData.getUserSubscription.canLikeContent)
            setCanUserReplyToContent(subscriptionData.getUserSubscription.canReplyToContent)
        }
        /* eslint-disable-next-line */
    }, [subscriptionData])

    if (subscriptionError) {
        throw new Error(`Unable to retrieve user details - please try again, ${subscriptionError}`)
    }

    useEffect(() => {
        async function setPermissions() {
            const userPermissions = await getUserPermissions()
            setIsUserAdmin(userPermissions.isAdmin)
            setPlatformAppReviewer(userPermissions.isPlatformAppReviewer)
        }
        setPermissions()
        // eslint-disable-next-line
    }, [])

    const freshUser = subscriptionData?.getUserSubscription as User

    const userAvailable = !!freshUser?.username

    /* We need to be a little careful about timings here. We don't want to
     * render the children until the user is available. And the user isn't
     * fully available until it has been set in recoil. So we need to wait
     * for that to happen before rendering the children.
     */
    let display = <Loader status="pending" />
    if (userAvailable && !subscriptionLoading) {
        display = <>{children}</>
    }

    // Possible states:
    // - User has does not have a referrer or a subscription => show welcome and email us page
    // - User has a referrer and a subscription => full access
    // - User has a referrer and does not have a subscription => show subscription page
    // Do not use routing for this.

    if (subscriptionLoading) {
        return <LoadingWelcome />
    }

    if (userAvailable && user?.shouldProvisionAccess) {
        return <>{children}</>
    }

    if (
        userAvailable &&
        !user.shouldProvisionAccess &&
        user?.referrer?.priceId &&
        !subscriptionLoading
    ) {
        return <PaymentPageRedirect />
    }

    if (userAvailable && !user.shouldProvisionAccess && !user?.referrer?.priceId) {
        // Show no subscription page
        return <NoActiveSubscription />
    }

    return (
        <div className="full-screen">
            <LoadingWelcome />
        </div>
    )
}

export default AppInitialisationContainer
